import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { listenToRideMessages, sendRideMessage } from '../services/firebase';
import useScrollLock from '../utils/useScrollLock';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

export default function ChatPanel({ rideId, userId, role, t, open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const online = useNetworkStatus();
  const endRef = useRef(null);
  useScrollLock(open);

  useEffect(() => {
    if (!open || !rideId) return undefined;
    return listenToRideMessages(rideId, setMessages);
  }, [open, rideId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  if (!open) return null;

  const send = async () => {
    if (!text.trim() || sending || !online) return;
    setSending(true);
    try {
      await sendRideMessage(rideId, userId, text, role);
      setText('');
    } catch {
      toast.error(t('chat.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="chat-backdrop" role="presentation" onClick={onClose}>
      <div className="chat-panel" onClick={(e) => e.stopPropagation()}>
        <div className="chat-panel-head">
          <strong>{t('chat.title')}</strong>
          <button type="button" className="chat-close" onClick={onClose}>✕</button>
        </div>
        <div className="chat-messages">
          {messages.length === 0 && <p className="text-muted chat-empty">{t('chat.empty')}</p>}
          {messages.map((m) => (
            <div key={m.id} className={`chat-bubble${m.senderId === userId ? ' mine' : ''}`}>
              <div className="chat-bubble-text">{m.text}</div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="chat-input-row">
          <input
            type="text"
            value={text}
            placeholder={t('chat.placeholder')}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          />
          <button type="button" className="btn btn-primary chat-send" disabled={sending || !text.trim() || !online} onClick={send}>
            {t('chat.send')}
          </button>
        </div>
      </div>
    </div>
  );
}
